import React, { useState, useEffect } from 'react'
import { Button, Form, Row, Col, Divider, Space, Select, Alert } from 'antd'
import {
  errorNotification,
  successNotification,
  warningNotification,
  carMakesUrl,
  carModelsUrl,
  customDutyBodyTypesUrl,
  fuelTypesUrl,
  driveTrainsUrl,
  originsUrl,
  getRequest,
  postRequest,
  putRequest,
  customDutiesUrl,
  dutyCheckMsrpUrl,
  conditionsUrl
} from '../../config'
import BasicInput from '../../components/Forms/BasicInput'
import SelectInput from '../../components/Forms/SelectInput'
import SelectSearch from '../../components/Forms/SelectInputInteractive'
import PropTypes from 'prop-types'

// In-scope market currencies for AI MSRP estimation. The backend resolves the currency
// from the car's origin when MSRP is left blank; this selector only matters when the
// operator overrides the MSRP manually.
const currencyOptions = ['CNY', 'USD', 'JPY', 'KRW', 'EUR', 'AED'].map(c => ({
  value: c,
  label: c
}))

const UpdatePage = props => {
  const { carDetail } = props
  const [loading, setLoading] = useState(false)
  const [catLoading, setCatLoading] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [serverErrors, setServerErrors] = useState({})

  const [payload, setPayload] = useState({
    customer: {
      phone: carDetail.customer?.phone || '',
      email: carDetail.customer?.email || '',
      momoPhone: carDetail.customer?.momoPhone || '',
      momoName: carDetail.customer?.momoName || '',
      momoNetwork: carDetail.customer?.momoNetwork || ''
    },
    make: carDetail.make?.name || carDetail.make || '',
    model: carDetail.model?.name || carDetail.model || '',
    condition: carDetail.condition?.id || carDetail.condition || '',
    fuelType: carDetail.fuelType?.id || carDetail.fuelType || '',
    origin: carDetail.origin?.id || carDetail.origin || '',
    bodyType: carDetail.bodyType?.id || carDetail.bodyType || '',
    driveTrain: carDetail.driveTrain?.id || carDetail.driveTrain || '',
    trim: carDetail.trim || '',
    carYear: carDetail.carYear || '',
    chassisNumber: carDetail.chassisNumber || '',
    engineSize: carDetail.engineSize || '',
    passengers: carDetail.passengers || 5,
    isSpecialPurpose: carDetail.isSpecialPurpose || false,
    // Pre-filled from the record; clear it to have the AI refetch the MSRP on save.
    msrpInUsd: carDetail.msrpInUsd || '',
    msrpCurrency: carDetail.msrpCurrency || 'USD',
    status: carDetail.status || 'PENDING'
  })

  const [checking, setChecking] = useState(false)

  /**
   * Ask the server for this car's MSRP, using the SAME resolution the duty assistant runs when it
   * finalizes: the customs whitelist first, then the validated AI lookup — a second-pass audit
   * that re-checks the currency and the amount and retries once when they do not hold up.
   *
   * The car is passed by id; everything it needs is already saved on the record. Save any edits
   * first, or the lookup runs against what is on the server rather than what is on screen.
   */
  const checkMsrp = async () => {
    setChecking(true)
    const res = await postRequest(dutyCheckMsrpUrl(carDetail.id), {}, 'MSRP', () => {})
    setChecking(false)
    if (!res.success) {
      errorNotification(
        res.error?.response?.data?.message || 'Could not find a reliable MSRP for this car.'
      )
      return
    }
    const { msrpInUsd, msrpCurrency } = res.data.car
    setPayload(current => ({ ...current, msrpInUsd, msrpCurrency }))
    // Said out loud, because the whole point of the button is the cross-check: a figure that
    // failed its audit must not look identical to one that passed.
    if (res.data.validated) {
      successNotification(`MSRP ${msrpCurrency} ${msrpInUsd} — cross-check passed.`)
    } else {
      warningNotification(
        `MSRP ${msrpCurrency} ${msrpInUsd} saved, but the cross-check did NOT pass. Verify it before quoting.`
      )
    }
  }

  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  // Free-text search queries for the make/model search-selects (make is upper-cased).
  const [makeQuery, setMakeQuery] = useState('')
  const [modelQuery, setModelQuery] = useState('')
  const [bodyTypes, setBodyTypes] = useState([])
  const [fuelTypes, setFuelTypes] = useState([])
  const [driveTrains, setDriveTrains] = useState([])
  const [origins, setOrigins] = useState([])
  const [conditions, setConditions] = useState([])

  const getYears = () => {
    const years = []
    for (let i = new Date().getFullYear() + 1; i >= 1960; i--) {
      years.push({ value: i, label: i })
    }
    return years
  }

  const fetchData = async (url, setter, label) => {
    const response = await getRequest(`${url}/all`, label, setCatLoading)
    if (response.success) {
      setter(
        response.data.map(item => ({
          value: item.id,
          label: item.name || item.country || item.country_name
        }))
      )
    }
  }

  // Makes use the brand NAME as the submitted value (so a free-text brand can be sent), while
  // retaining the id so we can still fetch a make's models from the `for-make` endpoint.
  const fetchMakes = async () => {
    const response = await getRequest(`${carMakesUrl}/all`, 'makes', setCatLoading)
    if (response.success) {
      setMakes(
        response.data.map(item => ({
          value: item.name,
          label: item.name,
          id: item.id
        }))
      )
    }
  }

  useEffect(() => {
    fetchMakes()
    fetchData(customDutyBodyTypesUrl, setBodyTypes, 'bodyTypes')
    fetchData(fuelTypesUrl, setFuelTypes, 'fuelTypes')
    fetchData(driveTrainsUrl, setDriveTrains, 'driveTrains')
    fetchData(originsUrl, setOrigins, 'origins')
    fetchData(conditionsUrl, setConditions, 'conditions')
  }, [])

  const getCarsModels = async makeId => {
    const response = await postRequest(
      `${carModelsUrl}/for-make`,
      { make: makeId },
      'models',
      setCatLoading
    )
    if (response.success) {
      setModels(
        response.data.map(item => ({ value: item.name, label: item.name }))
      )
    }
  }

  // Resolve the make NAME to its id from the loaded makes list and load its models. Runs once
  // makes arrive, so the pre-filled make on an existing record gets its models too.
  useEffect(() => {
    if (!payload.make) {
      setModels([])
      return
    }
    const found = makes.find(
      m => (m.value || '').toUpperCase() === payload.make.toUpperCase()
    )
    if (found) getCarsModels(found.id)
    else setModels([])
  }, [payload.make, makes])

  const handleInputChange = (field, isCustomer = false) => event => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value
    if (isCustomer) {
      setPayload({
        ...payload,
        customer: { ...payload.customer, [field]: value }
      })
    } else {
      setPayload({ ...payload, [field]: value })
    }
  }

  const handleSelect = field => value => {
    setPayload({ ...payload, [field]: value })
  }

  // Make/Model are searchable selects that also accept free text: the typed query is appended
  // as a selectable option so a brand/model not yet in the list can still be submitted (the
  // backend get-or-creates it). Make is upper-cased; changing it clears the model. The models
  // effect (keyed on payload.make) reloads suggestions for the new make.
  const handleMake = value => {
    setPayload({ ...payload, make: value, model: '' })
    setMakeQuery('')
    setModelQuery('')
  }

  const handleModel = value => {
    setPayload({ ...payload, model: value })
    setModelQuery('')
  }

  const handleMakeSearch = value => setMakeQuery((value || '').toUpperCase())
  const handleModelSearch = value => setModelQuery(value || '')

  // Returns the option list plus, when needed, the typed query and the current value as
  // selectable options so free-text entries render and submit correctly.
  const withFreeText = (list, query, current) => {
    const options = list.map(o => ({ value: o.value, label: o.label }))
    const ensure = v => {
      const val = (v || '').toString().trim()
      if (
        val &&
        !options.some(o => (o.value || '').toString().toUpperCase() === val.toUpperCase())
      ) {
        options.push({ value: val, label: val })
      }
    }
    ensure(query)
    ensure(current)
    return options
  }

  const handleSubmit = async () => {
    if (loading) return // guard against duplicate submits during the sync AI fetch
    setLoading(true)
    // MSRP is optional. Strip formatting; a blank value must reach the backend as null
    // (which triggers the AI fetch), never '' or NaN, which the DecimalField rejects.
    const raw = String(payload.msrpInUsd ?? '').replace(/[^0-9.-]/g, '')
    const msrp = raw === '' ? null : Number(raw)
    const res = await putRequest(
      `${customDutiesUrl}/${carDetail.id}/`,
      {
        ...payload,
        make: payload.make,
        model: payload.model,
        condition: payload.condition,
        fuelType: payload.fuelType,
        origin: payload.origin,
        bodyType: payload.bodyType,
        driveTrain: payload.driveTrain,
        msrpInUsd: Number.isFinite(msrp) ? msrp : null
      },
      'duty request',
      setLoading
    )
    if (res.success) {
      const ai = res.data && res.data.aiMsrp
      const conf =
        ai && ai.confidenceScore != null
          ? ` (AI confidence ${Math.round(ai.confidenceScore * 100)}%)`
          : ''
      successNotification(`Request updated${conf}.`)
      props.handleCancel()
    } else if (res.error && res.error.response) {
      // AI failure / validation error -> nothing was persisted. Keep the form open with
      // the entered values and surface the reason.
      const data = res.error.response.data || {}
      if (data.message) errorNotification(data.message)
      setServerErrors(data)
    }
  }

  return (
    <Form layout="vertical">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Clear the MSRP to have AI refetch it on save (in the car's original-market currency). VIN/chassis lookup applies to USA & Canada only."
      />
      <Divider orientation="left">Customer Details</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <BasicInput
            label="Phone"
            value={payload.customer.phone}
            handleChange={handleInputChange('phone', true)}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Email"
            value={payload.customer.email}
            handleChange={handleInputChange('email', true)}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Momo Name"
            value={payload.customer.momoName}
            handleChange={handleInputChange('momoName', true)}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <BasicInput
            label="Momo Phone"
            value={payload.customer.momoPhone}
            handleChange={handleInputChange('momoPhone', true)}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Momo Network"
            value={payload.customer.momoNetwork}
            handleChange={handleInputChange('momoNetwork', true)}
          />
        </Col>
      </Row>

      <Divider orientation="left">Vehicle Details</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <SelectSearch
            label="Make"
            placeholder="Type or select a make"
            value={payload.make}
            onChange={handleMake}
            optionList={withFreeText(makes, makeQuery, payload.make)}
            showSearch={true}
            onSearch={handleMakeSearch}
            searchValue={makeQuery}
            loading={catLoading}
            required
          />
        </Col>
        <Col span={8}>
          <SelectSearch
            label="Model"
            placeholder="Type or select a model"
            value={payload.model}
            onChange={handleModel}
            optionList={withFreeText(models, modelQuery, payload.model)}
            showSearch={true}
            onSearch={handleModelSearch}
            searchValue={modelQuery}
            loading={catLoading}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Year"
            value={payload.carYear}
            onChange={handleSelect('carYear')}
            optionList={getYears()}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <BasicInput
            label="Trim"
            value={payload.trim}
            handleChange={handleInputChange('trim')}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Chassis Number"
            value={payload.chassisNumber}
            handleChange={handleInputChange('chassisNumber')}
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Engine Size (L)"
            inputType="number"
            value={payload.engineSize}
            handleChange={handleInputChange('engineSize')}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <SelectInput
            label="Body Type"
            value={payload.bodyType}
            onChange={handleSelect('bodyType')}
            optionList={bodyTypes}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Fuel Type"
            value={payload.fuelType}
            onChange={handleSelect('fuelType')}
            optionList={fuelTypes}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Drive Train"
            value={payload.driveTrain}
            onChange={handleSelect('driveTrain')}
            optionList={driveTrains}
            required
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <SelectInput
            label="Origin"
            value={payload.origin}
            onChange={handleSelect('origin')}
            optionList={origins}
            required
          />
        </Col>
        <Col span={8}>
          <SelectInput
            label="Condition"
            value={payload.condition}
            onChange={handleSelect('condition')}
            optionList={conditions}
            required
          />
        </Col>
        <Col span={8}>
          <BasicInput
            label="Passengers"
            inputType="number"
            value={payload.passengers}
            handleChange={handleInputChange('passengers')}
          />
        </Col>
      </Row>
      <Divider orientation="left">MSRP (optional) & Status</Divider>
      <Row gutter={16} align="bottom">
        <Col span={6}>
          <BasicInput
            label="MSRP — leave blank for AI"
            inputType="number"
            value={payload.msrpInUsd}
            handleChange={handleInputChange('msrpInUsd')}
          />
        </Col>
        {/*
          Only when there is no MSRP yet.

          The server refuses it too — a button that is merely hidden still leaves the endpoint
          open, and overwriting a price somebody reviewed (or typed) with a fresh guess is the one
          thing a quality-control action must not do on a mis-click.
        */}
        {!payload.msrpInUsd ? (
          <Col span={6}>
            <Form.Item label=" " colon={false}>
              <Button onClick={checkMsrp} loading={checking} block>
                Check MSRP
              </Button>
            </Form.Item>
          </Col>
        ) : null}
        <Col span={5}>
          <SelectInput
            label="Currency (only if MSRP entered)"
            value={payload.msrpCurrency}
            onChange={handleSelect('msrpCurrency')}
            optionList={currencyOptions}
          />
        </Col>
        <Col span={7}>
          <Form.Item label="Status">
            <Select value={payload.status} onChange={handleSelect('status')}>
              <Select.Option value="PENDING">PENDING</Select.Option>
              <Select.Option value="PROCESSING">PROCESSING</Select.Option>
              <Select.Option value="COMPLETED">COMPLETED</Select.Option>
              <Select.Option value="CANCELLED">CANCELLED</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Special Purpose">
            <input
              type="checkbox"
              checked={payload.isSpecialPurpose}
              onChange={handleInputChange('isSpecialPurpose')}
            />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ marginTop: '20px', textAlign: 'right' }}>
        <Space>
          <Button onClick={props.handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Saving…' : 'Update Request'}
          </Button>
        </Space>
      </div>
    </Form>
  )
}

UpdatePage.propTypes = {
  carDetail: PropTypes.object,
  handleCancel: PropTypes.func
}

export default UpdatePage
